import { forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Slide from "@mui/material/Slide";
import { useTheme } from "@emotion/react";
import { IoClose } from "react-icons/io5";

// Brand palette (Dejlah primary blue) — kept in sync with the users/POS forms.
export const BLUE = "#0C86B6";
export const BLUE_DARK = "#0a6b92";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// A titled, bordered group that visually separates each part of the form.
export const SectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      border: "1px solid #edf0f3",
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}
  >
    {title && (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(12,134,182,0.12)",
              color: BLUE,
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2a37" }}>
          {title}
        </Typography>
      </Stack>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

// A plain white panel for content that is not a field grid (tables, lists...).
export const PlainCard = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      border: "1px solid #edf0f3",
      borderRadius: 3,
      p: { xs: 1.5, sm: 2 },
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Shared modern dialog shell: gradient header, grey content area and a sticky
 * footer. Goes full screen on phones/tablets so forms stay usable on mobile.
 *
 * Pass `onSubmit` to render the content inside a <form> with a save button.
 * Omit it for read-only dialogs (the footer then only shows the close button).
 */
const FormDialog = ({
  open,
  onClose,
  icon,
  title,
  subtitle,
  maxWidth = "sm",
  onSubmit,
  saving = false,
  saveText = "حفظ",
  cancelText = "إلغاء",
  closeText = "اغلاق",
  extraActions,
  hideFooter = false,
  children,
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const footer = hideFooter ? null : (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 2,
        bgcolor: "#ffffff",
        borderTop: "1px solid #edf0f3",
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
        flexShrink: 0,
      }}
    >
      {extraActions}
      <Button
        onClick={onClose}
        variant="outlined"
        fullWidth={smallScreen && !!onSubmit}
        sx={{
          color: "#475467",
          borderColor: "#d0d5dd",
          px: 3,
          "&:hover": { borderColor: "#98a2b3", bgcolor: "#f9fafb" },
        }}
      >
        {onSubmit ? cancelText : closeText}
      </Button>
      {onSubmit && (
        <LoadingButton
          type="submit"
          variant="contained"
          loading={saving}
          fullWidth={smallScreen}
          sx={{
            bgcolor: BLUE,
            px: 4,
            boxShadow: "0 4px 12px rgba(12,134,182,0.3)",
            "&:hover": { bgcolor: BLUE_DARK },
          }}
        >
          {saveText}
        </LoadingButton>
      )}
    </Box>
  );

  const body = (
    <>
      <DialogContent sx={{ bgcolor: "#f4f6f8", p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.5}>{children}</Stack>
      </DialogContent>
      {footer}
    </>
  );

  return (
    <Dialog
      open={!!open}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      fullScreen={smallScreen}
      maxWidth={maxWidth}
      dir="rtl"
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        sx: {
          borderRadius: smallScreen ? 0 : 3,
          maxHeight: smallScreen ? "100%" : "94vh",
          overflow: "hidden",
          "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BLUE} 0%, #13a2d4 100%)`,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2.25 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontSize: { xs: 22, sm: 26 },
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: 17, sm: 20 },
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  fontSize: { xs: 12, sm: 13 },
                  mt: 0.25,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <IoClose />
        </IconButton>
      </Box>

      {onSubmit ? (
        <form
          onSubmit={onSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {body}
        </form>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {body}
        </Box>
      )}
    </Dialog>
  );
};

export default FormDialog;
